import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  consentApi,
  organizationConsentModeApi,
  RequestError,
  type ConsentDocumentResponse,
  type TenantConsentModeResponse,
} from '../api/client';
import {
  selectRequiredDocuments,
  buildConsentItems,
  type ConsentItem,
} from '../utils/consentItems';
import ConsentChoicesTable from '../components/ConsentChoicesTable';



/** EMP-S05 同意と利用範囲（API 連携）+ テナント現モード表示（読み取り専用） */
export default function SettingsConsent() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  /**
   * 同意項目は公開文書を起点に作る（課題 #77）。
   * 履歴から作ると、未同意の人には項目が出ず同意する手段が無くなる。
   */
  const [items, setItems] = useState<ConsentItem[]>([]);
  const [documents, setDocuments] = useState<ConsentDocumentResponse[]>([]);
  const [tenantMode, setTenantMode] = useState<TenantConsentModeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    Promise.all([
      consentApi.list(token),
      consentApi.getDocuments(token),
      consentApi.getMasterModes(token),
      organizationConsentModeApi.getSelf(token).catch((err: unknown) => {
        // 旧版 BE で 404 の場合はモード表示を非表示にするだけで画面全体は出す
        if (err instanceof RequestError && err.status === 404) {
          return null;
        }
        throw err;
      }),
    ])
      .then(([listRes, docsRes, modesRes, modeRes]) => {
        const required = selectRequiredDocuments(modesRes, modeRes?.consent_mode ?? null);
        setItems(buildConsentItems(required, listRes.data ?? []));
        setDocuments(Array.isArray(docsRes) ? docsRes : []);
        setTenantMode(modeRes);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        setItems([]);
        setDocuments([]);
        setTenantMode(null);
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="page settings-sub-page">
      <h1>{t('settings.consent.title')}</h1>
      <p className="settings-intro">{t('settings.consent.intro')}</p>

      {error && (
        <div className="settings-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {loading && (
        <div className="settings-card">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          {tenantMode && (
            <section className="settings-card settings-consent-mode" aria-labelledby="tenant-mode-heading">
              <h3 id="tenant-mode-heading">{t('settings.consent.tenantMode.title')}</h3>
              <p className="settings-intro">
                {t('settings.consent.tenantMode.intro', { name: tenantMode.name })}
              </p>
              <dl className="settings-consent-mode-readonly">
                <dt>{t('settings.consent.tenantMode.currentLabel')}</dt>
                <dd>
                  <span className={`settings-badge settings-consent-mode-badge-${tenantMode.consent_mode}`}>
                    {t(`settings.consent.tenantMode.${tenantMode.consent_mode}`)}
                  </span>
                </dd>
              </dl>
              <p className="settings-consent-mode-hint">
                {t(`settings.consent.tenantMode.${tenantMode.consent_mode}Desc`)}
              </p>
              <p className="settings-consent-mode-hint">
                {t('settings.consent.tenantMode.switchHint')}
              </p>
            </section>
          )}

          <div className="settings-card">
            {/* 本文を開いてから同意する（課題 #77）。本文は各行の「本文を読む」で開く */}
            <ConsentChoicesTable
              items={items}
              documents={documents}
              showDate
              label={(item) => item.document.purpose_label_ja || item.document.purpose}
              onChanged={load}
            />
            {items.length === 0 && <p className="settings-empty">{t('settings.consent.noItems')}</p>}
          </div>
        </>
      )}

      <p className="settings-back">
        <Link to="/dashboard/settings">{t('common.back')} → {t('settings.backToSettings')}</Link>
      </p>
    </div>
  );
}
